import React, { useEffect, useState } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import GenericMapTiered from '../charts/GenericMapTiered';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { PageHeader } from '../layout/PageHeader';


interface DataMetrics {
  currentValue: number;
  californiaAverage: number;
  year: number;
  variable: string;
  minValue?: number;
  maxValue?: number;
}

interface IncomeMetrics {
  currentIncome: number;
  californiaAverage: number;
  incomeRatio: number;
  year: number;
}

const MapsAnalysis: React.FC = () => {
  const { selectedLocation } = useLocation();
  const currentLocation = selectedLocation || 'Berkeley';
  
  // Debug logging
  
  const [, setIncomeMetrics] = useState<IncomeMetrics | null>(null);
  const [dataMetrics, setDataMetrics] = useState<DataMetrics | null>(null);
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');

  // Reset metrics when location changes
  useEffect(() => {
    setIncomeMetrics(null);
    setDataMetrics(null);
  }, [currentLocation]);




  const handleDataMetricsLoaded = (metrics: DataMetrics) => {
    setDataMetrics(metrics);
  };

  const toggleView = (view: string) => {
    setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Maps"
        subtitle="Interactive maps for all application content with geographic visualization of socioeconomic, environmental, and climate data"
        variant="maps"
      />

      {/* Interactive Map Section */}
      <ChartLayout
        title="Interactive Data Map (more maps coming soon!)"
        description="Geographic visualization of socioeconomic data by census tract. Use the dropdown to switch between different data types."
        chartType="map"
        layout="vertical"
        keyMetrics={
          dataMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  dataMetrics.variable === 'income'
                    ? `$${Math.round(dataMetrics.currentValue).toLocaleString()}`
                    : dataMetrics.variable === 'ev_adoption'
                    ? `${dataMetrics.currentValue.toFixed(1)} EVs/100`
                    : dataMetrics.variable === 'total_chargers'
                    ? `${Math.round(dataMetrics.currentValue).toLocaleString()} chargers`
                    : dataMetrics.variable === 'vehicles_per_household'
                    ? `${dataMetrics.currentValue.toFixed(2)} vehicles/HH`
                    : dataMetrics.variable === 'population_density'
                    ? `${Math.round(dataMetrics.currentValue).toLocaleString()} per sq mi`
                    : dataMetrics.variable === 'political_lean'
                    ? `${dataMetrics.currentValue.toFixed(1)} pts (D−R)`
                    : dataMetrics.variable === 'carbon_footprint'
                    ? `${dataMetrics.currentValue.toFixed(1)} MT CO₂e/HH`
                    : `${Math.round(dataMetrics.currentValue).toLocaleString()}`
                }
                label={
                  dataMetrics.variable === 'income'
                    ? 'Median Household Income'
                    : dataMetrics.variable === 'ev_adoption'
                    ? 'EVs per 100 People'
                    : dataMetrics.variable === 'total_chargers'
                    ? 'Total EV Chargers'
                    : dataMetrics.variable === 'vehicles_per_household'
                    ? 'Vehicles per Household'
                    : dataMetrics.variable === 'population_density'
                    ? 'Population Density'
                    : dataMetrics.variable === 'political_lean'
                    ? 'Political Lean (D − R)'
                    : dataMetrics.variable === 'carbon_footprint'
                    ? 'Average Household Carbon Footprint'
                    : 'Value'
                }
              />
              <KeyMetric
                value={
                  dataMetrics.variable === 'income'
                    ? `$${Math.round(dataMetrics.californiaAverage).toLocaleString()}`
                    : dataMetrics.variable === 'ev_adoption'
                    ? `${dataMetrics.californiaAverage.toFixed(1)} EVs/100`
                    : dataMetrics.variable === 'total_chargers'
                    ? `${Math.round(dataMetrics.californiaAverage).toLocaleString()} chargers`
                    : dataMetrics.variable === 'vehicles_per_household'
                    ? `${dataMetrics.californiaAverage.toFixed(2)} vehicles/HH`
                    : dataMetrics.variable === 'population_density'
                    ? `${Math.round(dataMetrics.californiaAverage).toLocaleString()} per sq mi`
                    : dataMetrics.variable === 'political_lean'
                    ? `${dataMetrics.californiaAverage.toFixed(1)} pts (D−R)`
                    : dataMetrics.variable === 'carbon_footprint'
                    ? `${dataMetrics.californiaAverage.toFixed(1)} MT CO₂e/HH`
                    : `${Math.round(dataMetrics.californiaAverage).toLocaleString()}`
                }
                label="California Average"
              />
              <KeyMetric value={dataMetrics.year.toString()} label="Data Year" />
            </KeyMetrics>
          ) : undefined
        }
              chartContent={
                <GenericMapTiered 
                  location={currentLocation}
                  onDataLoaded={handleDataMetricsLoaded}
                />
              }
        overviewContent={
          dataMetrics ? (
            <OverviewContent
              title={`${dataMetrics.variable === 'income' 
                ? 'Median Household Income' 
                : dataMetrics.variable === 'ev_adoption'
                ? 'Electric Vehicles per 100 People'
                : dataMetrics.variable === 'total_chargers'
                ? 'Total EV Chargers'
                : 'Mean Travel Time to Work'} Overview`}
              description={`This analysis examines ${dataMetrics.variable === 'income' 
                ? 'median household income' 
                : dataMetrics.variable === 'ev_adoption'
                ? 'electric vehicle adoption rates'
                : dataMetrics.variable === 'total_chargers'
                ? 'electric vehicle charging infrastructure'
                : 'mean travel time to work'} patterns and trends.`}
            >
              {dataMetrics.variable === 'income' ? [
                `${currentLocation} has a median household income of $${dataMetrics.currentValue.toLocaleString()}, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of $${dataMetrics.californiaAverage.toLocaleString()}.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has higher median household income than the state average, suggesting greater economic prosperity in the area.`
                  : `This indicates that ${currentLocation} has lower median household income than the state average, highlighting potential economic challenges or cost-of-living differences.`,
                `The data is from ${dataMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and economic information.`
              ] : dataMetrics.variable === 'ev_adoption' ? [
                `${currentLocation} has ${dataMetrics.currentValue.toFixed(1)} electric vehicles per 100 people, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage.toFixed(1)} EVs per 100 people.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has higher electric vehicle adoption than the state average, suggesting strong support for clean transportation.`
                  : `This indicates that ${currentLocation} has lower electric vehicle adoption than the state average, highlighting opportunities for EV promotion and infrastructure development.`,
                `The data is from 2023 EV registration data, providing current information on electric vehicle adoption patterns.`
              ] : dataMetrics.variable === 'total_chargers' ? [
                `${currentLocation} has ${dataMetrics.currentValue.toFixed(0)} total EV chargers, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage.toFixed(0)} chargers.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has more EV charging infrastructure than the state average, suggesting strong support for electric vehicle adoption.`
                  : `This indicates that ${currentLocation} has fewer EV chargers than the state average, highlighting opportunities for charging infrastructure development.`,
                `The data is from 2023 EV charging infrastructure data, providing current information on charging station distribution.`
              ] : dataMetrics.variable === 'carbon_footprint' ? (() => {
                const paragraphs = [
                  `${currentLocation} has an average household carbon footprint of ${dataMetrics.currentValue.toFixed(1)} MT CO₂e/HH, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage.toFixed(1)} MT CO₂e/HH.`
                ];

                if (dataMetrics.minValue !== undefined && dataMetrics.maxValue !== undefined) {
                  const percentDiff = ((dataMetrics.maxValue / dataMetrics.minValue - 1) * 100).toFixed(0);
                  paragraphs.push(
                    `Among census tracts in ${currentLocation}, household carbon footprints range from ${dataMetrics.minValue.toFixed(1)} MT CO₂e/HH (lowest) to ${dataMetrics.maxValue.toFixed(1)} MT CO₂e/HH (highest), representing a ${percentDiff}% difference. This variation highlights significant disparities in consumption patterns across different neighborhoods. The highest-footprint tracts may benefit most from targeted climate action programs, while the lowest-footprint tracts can serve as models for sustainable consumption practices.`,
                    `The variation in carbon footprints across census tracts reflects differences in income levels, housing types, transportation options, and consumption patterns. Understanding these disparities is crucial for developing equitable climate policies that address the needs of all communities while targeting areas with the highest emissions potential.`
                  );
                } else {
                  paragraphs.push(
                    dataMetrics.currentValue > dataMetrics.californiaAverage 
                      ? `This indicates that ${currentLocation} has higher household carbon footprints than the state average, suggesting greater emissions from transportation, housing, food, goods, and services consumption.`
                      : `This indicates that ${currentLocation} has lower household carbon footprints than the state average, suggesting more sustainable consumption patterns and potential for sharing best practices.`
                  );
                }

                paragraphs.push(`The data is from 2023 EcoDataLab Consumption-Based Emissions Inventory, providing comprehensive household-level emissions estimates.`);
                return paragraphs;
              })() : dataMetrics.variable === 'vehicles_per_household' ? [
                `${currentLocation} has ${dataMetrics.currentValue.toFixed(2)} vehicles per household, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage.toFixed(2)} vehicles per household.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has higher vehicle ownership than the state average, suggesting greater dependence on personal vehicles for transportation.`
                  : `This indicates that ${currentLocation} has lower vehicle ownership than the state average, suggesting better access to alternative transportation options or higher density living.`,
                `The data is from ${dataMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and transportation information.`
              ] : dataMetrics.variable === 'population_density' ? [
                `${currentLocation} has a population density of ${dataMetrics.currentValue.toLocaleString()} people per square mile, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage.toLocaleString()} people per square mile.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has higher population density than the state average, suggesting more urbanized areas with potential for efficient public transit and walkable communities.`
                  : `This indicates that ${currentLocation} has lower population density than the state average, suggesting more suburban or rural areas with different transportation and land use patterns.`,
                `The data is from ${dataMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and geographic information.`
              ] : dataMetrics.variable === 'political_lean' ? [
                `${currentLocation} has a political lean of ${dataMetrics.currentValue.toFixed(1)} points (Democrat minus Republican), which is ${dataMetrics.currentValue > 0 ? 'more Democratic' : 'more Republican'} than the California average of ${dataMetrics.californiaAverage.toFixed(1)} points.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has stronger Democratic leanings than the state average, suggesting potential support for climate action policies.`
                  : `This indicates that ${currentLocation} has weaker Democratic leanings than the state average, highlighting the importance of bipartisan climate solutions.`,
                `The data is from ${dataMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and political information.`
              ] : [
                `${currentLocation} has an average commute time of ${dataMetrics.currentValue.toFixed(1)} minutes, which is ${((dataMetrics.currentValue / dataMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${dataMetrics.californiaAverage} minutes.`,
                dataMetrics.currentValue > dataMetrics.californiaAverage 
                  ? `This indicates that ${currentLocation} has longer average commute times than the state average, suggesting potential transportation challenges or longer distances to employment centers.`
                  : `This indicates that ${currentLocation} has shorter average commute times than the state average, suggesting better access to employment or more efficient transportation options.`,
                `The data is from ${dataMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and economic information.`
              ]}
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          dataMetrics ? (
            <RelevanceContent
              title="Relevance to Climate Action Policy"
              description={`How ${dataMetrics.variable === 'income' 
                ? 'median household income' 
                : dataMetrics.variable === 'ev_adoption'
                ? 'electric vehicle adoption rates'
                : dataMetrics.variable === 'total_chargers'
                ? 'EV charging infrastructure'
                : dataMetrics.variable === 'carbon_footprint'
                ? 'household carbon footprints'
                : dataMetrics.variable === 'vehicles_per_household'
                ? 'vehicle ownership patterns'
                : dataMetrics.variable === 'population_density'
                ? 'population density'
                : dataMetrics.variable === 'political_lean'
                ? 'political leanings'
                : 'mean travel time to work'} data informs climate action planning and policy development.`}
            >
              {dataMetrics.variable === 'income' ? [
                'Income distribution data helps identify communities that may need additional support for climate adaptation and mitigation measures.',
                'Lower-income areas often face greater vulnerability to climate impacts and may require targeted assistance for energy efficiency upgrades.',
                'Understanding economic capacity helps prioritize climate investments and ensure equitable distribution of resources.',
                'Income data informs the design of climate programs that are accessible to all community members regardless of economic status.'
              ] : dataMetrics.variable === 'ev_adoption' ? [
                'EV adoption data reveals which communities are leading in clean transportation and where additional support is needed.',
                'Areas with low EV adoption may benefit from targeted incentives, charging infrastructure, and education programs.',
                'Understanding EV adoption patterns helps prioritize infrastructure investments and policy interventions.',
                'EV data informs transportation electrification strategies and helps track progress toward climate goals.'
              ] : dataMetrics.variable === 'total_chargers' ? [
                'EV charging infrastructure data reveals where charging stations are concentrated and where gaps exist.',
                'Areas with limited charging infrastructure may face barriers to EV adoption and need targeted investments.',
                'Understanding charging station distribution helps prioritize infrastructure investments and policy interventions.',
                'Charging infrastructure data informs transportation electrification strategies and helps track progress toward climate goals.'
              ] : dataMetrics.variable === 'carbon_footprint' ? [
                'Carbon footprint data reveals which communities have the highest emissions and greatest potential for reductions.',
                'Areas with high carbon footprints may benefit from targeted energy efficiency, transportation, and consumption reduction programs.',
                'Understanding consumption-based emissions helps prioritize climate investments and track progress toward emission reduction goals.',
                'Carbon footprint data informs comprehensive climate action planning that addresses all sources of household emissions.'
              ] : dataMetrics.variable === 'vehicles_per_household' ? [
                'Vehicle ownership data reveals transportation patterns and opportunities for sustainable mobility investments.',
                'Areas with high vehicle ownership may benefit from car-sharing programs, public transit improvements, and bike infrastructure.',
                'Understanding vehicle ownership patterns helps prioritize transportation investments and policy interventions.',
                'Vehicle ownership data informs land use planning to reduce transportation emissions through better job-housing balance.'
              ] : dataMetrics.variable === 'population_density' ? [
                'Population density data reveals opportunities for efficient public transit, walkable communities, and compact development.',
                'Higher density areas may benefit from transit-oriented development, bike infrastructure, and shared mobility services.',
                'Understanding population patterns helps prioritize infrastructure investments and sustainable development strategies.',
                'Population density data informs land use planning to reduce transportation emissions and promote climate-friendly development.'
              ] : dataMetrics.variable === 'political_lean' ? [
                'Political leanings data helps identify communities that may be more or less supportive of climate action policies.',
                'Understanding political preferences helps tailor climate communication strategies and policy approaches.',
                'Political data informs coalition building and helps identify opportunities for bipartisan climate solutions.',
                'Political leanings data helps prioritize climate investments in areas where policies are most likely to succeed.'
              ] : [
                'Commute patterns reveal transportation emissions hotspots and opportunities for sustainable transportation investments.',
                'Longer commute times often correlate with higher vehicle emissions and greater potential for emissions reductions.',
                'Understanding travel patterns helps prioritize public transit, bike infrastructure, and electric vehicle charging investments.',
                'Commute data informs land use planning to reduce transportation emissions through better job-housing balance.'
              ]}
            </RelevanceContent>
          ) : undefined
        }
        equityContent={
          dataMetrics ? (
            <EquityContent
              title="Equity Considerations"
              description={`Equity implications and considerations for ${dataMetrics.variable === 'income' 
                ? 'median household income' 
                : dataMetrics.variable === 'ev_adoption'
                ? 'electric vehicle adoption rates'
                : dataMetrics.variable === 'total_chargers'
                ? 'EV charging infrastructure'
                : dataMetrics.variable === 'carbon_footprint'
                ? 'household carbon footprints'
                : dataMetrics.variable === 'vehicles_per_household'
                ? 'vehicle ownership patterns'
                : dataMetrics.variable === 'population_density'
                ? 'population density'
                : dataMetrics.variable === 'political_lean'
                ? 'political leanings'
                : 'mean travel time to work'} data in climate planning.`}
            >
              {dataMetrics.variable === 'income' ? [
                'Income disparities can create unequal access to climate solutions like solar panels, electric vehicles, and energy efficiency upgrades.',
                'Lower-income communities may face higher energy burdens and need additional support for climate adaptation measures.',
                'Climate policies should consider economic impacts and ensure benefits reach all income levels.',
                'Targeted programs may be needed to address the intersection of economic and environmental justice.'
              ] : dataMetrics.variable === 'ev_adoption' ? [
                'EV adoption disparities can reflect economic inequities and unequal access to clean transportation technology.',
                'Lower-income communities may face barriers to EV adoption due to higher upfront costs and limited charging infrastructure.',
                'EV incentive programs should prioritize underserved communities to ensure equitable access to clean transportation.',
                'Understanding EV adoption patterns helps identify where additional support and infrastructure investments are needed.'
              ] : dataMetrics.variable === 'total_chargers' ? [
                'Charging infrastructure disparities can create unequal access to clean transportation technology.',
                'Lower-income communities may face barriers to EV adoption due to limited charging infrastructure availability.',
                'Charging infrastructure investments should prioritize underserved communities to ensure equitable access to clean transportation.',
                'Understanding charging station distribution helps identify where additional infrastructure investments are needed.'
              ] : dataMetrics.variable === 'carbon_footprint' ? [
                'Carbon footprint disparities can reflect unequal access to sustainable consumption options and clean technologies.',
                'Lower-income communities may face higher energy burdens and limited access to energy-efficient appliances and transportation.',
                'Climate policies should address consumption inequities and ensure all communities can participate in emissions reductions.',
                'Understanding carbon footprint patterns helps identify where additional support and sustainable development investments are needed.'
              ] : dataMetrics.variable === 'vehicles_per_household' ? [
                'Vehicle ownership disparities can reflect unequal access to transportation options and mobility choices.',
                'Lower-income communities may face barriers to alternative transportation due to limited public transit and bike infrastructure.',
                'Transportation investments should prioritize underserved communities to ensure equitable access to sustainable mobility options.',
                'Understanding vehicle ownership patterns helps identify where additional transportation infrastructure investments are needed.'
              ] : dataMetrics.variable === 'population_density' ? [
                'Population density disparities can reflect unequal access to efficient public services and sustainable development.',
                'Lower-density areas may face challenges accessing public transit, walkable communities, and shared services.',
                'Development policies should ensure equitable access to sustainable infrastructure regardless of population density.',
                'Understanding population patterns helps identify where additional infrastructure investments and development strategies are needed.'
              ] : dataMetrics.variable === 'political_lean' ? [
                'Political leanings can reflect different levels of support for climate action policies and environmental justice.',
                'Communities with different political preferences may need tailored approaches to climate communication and policy implementation.',
                'Climate policies should be designed to work across political divides and ensure benefits reach all communities.',
                'Understanding political patterns helps identify opportunities for bipartisan climate solutions and coalition building.'
              ] : [
                'Commute time disparities can reflect transportation inequities and limited access to sustainable transportation options.',
                'Longer commute times may disproportionately affect lower-income workers who cannot afford to live near job centers.',
                'Transportation investments should prioritize underserved communities with limited mobility options.',
                'Equitable transportation planning requires addressing both environmental and social justice considerations.'
              ]}
            </EquityContent>
          ) : undefined
        }
        methodologyContent={
          dataMetrics ? (
            <MethodologyContent
              title="Methodology"
              description={`${dataMetrics.variable === 'income' 
                ? 'Median Household Income' 
                : dataMetrics.variable === 'ev_adoption'
                ? 'Electric Vehicle Adoption'
                : dataMetrics.variable === 'total_chargers'
                ? 'Total EV Chargers'
                : dataMetrics.variable === 'carbon_footprint'
                ? 'Average Household Carbon Footprint'
                : dataMetrics.variable === 'vehicles_per_household'
                ? 'Vehicles per Household'
                : dataMetrics.variable === 'population_density'
                ? 'Population Density'
                : dataMetrics.variable === 'political_lean'
                ? 'Political Lean'
                : 'Mean Travel Time to Work'} analysis based on ${dataMetrics.variable === 'total_chargers' ? 'EV charging infrastructure' : dataMetrics.variable === 'carbon_footprint' ? 'EcoDataLab consumption-based emissions inventory' : dataMetrics.variable === 'ev_adoption' ? 'EV registration data' : 'American Community Survey'} data with geographic visualization.`}
              dataSources={dataMetrics.variable === 'income' ? [
                'American Community Survey 5-Year Estimates (ACS5)',
                'U.S. Census Bureau demographic data',
                'California census tract boundaries',
                'Median household income estimates (B19013_001E)'
              ] : dataMetrics.variable === 'ev_adoption' ? [
                'EV registration database',
                'California EV registration data',
                'California census tract boundaries',
                'EVs per 100 people by census tract'
              ] : dataMetrics.variable === 'total_chargers' ? [
                'EV charging infrastructure database',
                'California EV charging station data',
                'California census tract boundaries',
                'Total EV chargers by census tract'
              ] : dataMetrics.variable === 'carbon_footprint' ? [
                'EcoDataLab Consumption-Based Emissions Inventory (CBEI)',
                'Consumer Expenditures Survey (CE) from Bureau of Labor Statistics',
                'National Household Travel Survey (NHTS)',
                'Residential Energy Consumption Survey (RECS)',
                'US EPA USEEIO Model for consumption-to-emissions mapping',
                'US EPA eGrid emission factors for electricity',
                'US Census Bureau demographic and geographic data',
                'California census tract boundaries'
              ] : dataMetrics.variable === 'vehicles_per_household' ? [
                'American Community Survey 5-Year Estimates (ACS5)',
                'U.S. Census Bureau demographic data',
                'California census tract boundaries',
                'Vehicles per household estimates'
              ] : dataMetrics.variable === 'population_density' ? [
                'American Community Survey 5-Year Estimates (ACS5)',
                'U.S. Census Bureau demographic data',
                'California census tract boundaries',
                'Population density estimates (persons per square mile)'
              ] : dataMetrics.variable === 'political_lean' ? [
                'American Community Survey 5-Year Estimates (ACS5)',
                'U.S. Census Bureau demographic data',
                'California census tract boundaries',
                'Political lean estimates (Democrat minus Republican)'
              ] : [
                'American Community Survey 5-Year Estimates (ACS5)',
                'U.S. Census Bureau demographic data',
                'California census tract boundaries',
                'Mean travel time to work estimates (B08303_013E)'
              ]}
              methodology={dataMetrics.variable === 'income' ? [
                'B19013_001E: Median household income for all households',
                'Data represents the middle value where half of households earn more and half earn less',
                'Geographic visualization shows income distribution across census tracts',
                'Comparison calculated as percentage of California state average',
                '5-year estimates provide more reliable data for smaller geographic areas',
                'Income values adjusted for inflation to current year dollars'
              ] : dataMetrics.variable === 'ev_adoption' ? [
                'EVs per 100 people: Total electric vehicles divided by total population times 100',
                'Data represents the penetration rate of electric vehicles in each census tract',
                'Geographic visualization shows EV adoption patterns across census tracts',
                'Comparison calculated as percentage of California state average',
                '2023 data provides current information on electric vehicle adoption',
                'Includes all types of electric vehicles (BEV, PHEV, FCEV)'
              ] : dataMetrics.variable === 'total_chargers' ? [
                'Total EV chargers: Sum of Level 2 and DC Fast chargers by census tract',
                'Data represents the total number of EV charging stations in each tract',
                'Geographic visualization shows charging infrastructure distribution across census tracts',
                'Comparison calculated as percentage of California state average',
                '2023 data provides current information on charging station distribution',
                'Includes both public and private charging infrastructure'
              ] : dataMetrics.variable === 'carbon_footprint' ? [
                'Consumption-Based Emissions Inventory (CBEI) methodology developed by EcoDataLab',
                'Model estimates household consumption and associated emissions using multiple linear regression',
                'Primary data sources: Consumer Expenditures Survey (CE), National Household Travel Survey (NHTS), Residential Energy Consumption Survey (RECS)',
                'Model variables: household size, income, vehicle ownership, home ownership, education, home size, commute patterns, climate data',
                'Emissions calculated using US EPA USEEIO Model for consumption-to-emissions mapping',
                'Electricity emissions use EPA eGrid region emission factors scaled to local geography',
                'Transportation emissions based on vehicle miles traveled and fuel economy data',
                'Model results scaled to real-world data at state/local level for accuracy',
                'Data represents 2023 estimates of average household carbon footprint by census tract',
                'Values converted from grams to metric tons CO₂ equivalent per household',
                'Methodology accounts for all lifecycle emissions including production, transport, and consumption',
                'Limitations: assumes imports have same carbon intensity as domestic production, excludes government emissions'
              ] : dataMetrics.variable === 'vehicles_per_household' ? [
                'Vehicles per household: Total vehicles divided by total households',
                'Data represents the average number of vehicles owned per household in each census tract',
                'Geographic visualization shows vehicle ownership patterns across census tracts',
                'Comparison calculated as percentage of California state average',
                '5-year estimates provide more reliable data for smaller geographic areas',
                'Includes all types of vehicles (cars, trucks, motorcycles, etc.)'
              ] : dataMetrics.variable === 'population_density' ? [
                'Population density: Total population divided by land area in square miles',
                'Data represents the number of people per square mile in each census tract',
                'Geographic visualization shows population concentration patterns across census tracts',
                'Comparison calculated as percentage of California state average',
                '5-year estimates provide more reliable data for smaller geographic areas',
                'Land area excludes water bodies and other non-residential areas'
              ] : dataMetrics.variable === 'political_lean' ? [
                'Political lean: Democrat minus Republican vote share in recent elections',
                'Data represents the political orientation of each census tract',
                'Geographic visualization shows political preferences across census tracts',
                'Comparison calculated relative to California state average',
                '5-year estimates provide more reliable data for smaller geographic areas',
                'Based on recent federal and state election results'
              ] : [
                'B08303_013E: Mean travel time to work in minutes for workers 16 years and over',
                'Data represents the average commute time for employed residents',
                'Geographic visualization shows commute time distribution across census tracts',
                'Comparison calculated as percentage of California state average',
                '5-year estimates provide more reliable data for smaller geographic areas',
                'Commute times include all modes of transportation to work'
              ]}
            />
          ) : undefined
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}

      />


    </div>
  );
};

export default MapsAnalysis;
